import { z } from "zod";
import { ClaimableRoleSchema } from "./common.js";
/**
 * `TAKEOVER` binds the applicant's account to an existing GUEST participant
 * (the row flips GUEST → USER, preserving every historical expense
 * reference). `NEW` creates a fresh USER participant with no historical
 * expenses tied to the applicant.
 */
export const JoinRequestModeSchema = z.enum(["TAKEOVER", "NEW"]);
export const JoinRequestStatusSchema = z.enum([
    "PENDING",
    "APPROVED",
    "DENIED",
    "CANCELLED",
]);
/** Body of `POST /groups/{groupId}/join-requests`. */
export const SubmitJoinRequestRequestSchema = z.discriminatedUnion("mode", [
    z.strictObject({
        mode: z.literal("TAKEOVER"),
        targetParticipantId: z.string().uuid(),
        proposedRole: ClaimableRoleSchema.default("MEMBER"),
    }),
    z.strictObject({
        mode: z.literal("NEW"),
        displayName: z.string().min(1).max(64),
        proposedRole: ClaimableRoleSchema.default("MEMBER"),
    }),
]);
/**
 * Persisted join-request entity, surfaced to the OWNER review UI and to the
 * applicant's own pending-request list.
 */
export const JoinRequestSchema = z.strictObject({
    id: z.string().uuid(),
    groupId: z.string().uuid(),
    applicantUserId: z.string().uuid(),
    applicantDisplayName: z.string().min(1).max(64),
    mode: JoinRequestModeSchema,
    /** Set only when `mode === "TAKEOVER"`. */
    targetParticipantId: z.string().uuid().optional(),
    /** Set only when `mode === "NEW"`. */
    requestedDisplayName: z.string().min(1).max(64).optional(),
    proposedRole: ClaimableRoleSchema,
    status: JoinRequestStatusSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    decidedAt: z.string().datetime().optional(),
    decidedBy: z.string().uuid().optional(),
    finalRole: ClaimableRoleSchema.optional(),
    finalParticipantId: z.string().uuid().optional(),
});
/**
 * Body of `POST /groups/{groupId}/join-requests/{requestId}/approve`. Both
 * fields are optional: `role` defaults to the applicant's `proposedRole`,
 * and `displayName` only applies in `NEW` mode (in which case it overrides
 * the applicant's requested display name).
 */
export const ApproveJoinRequestRequestSchema = z.strictObject({
    role: ClaimableRoleSchema.optional(),
    displayName: z.string().min(1).max(64).optional(),
});
/** Response from `POST /groups/{groupId}/join-requests/{requestId}/approve`. */
export const ApproveJoinRequestResponseSchema = z.strictObject({
    groupId: z.string().uuid(),
    participantId: z.string().uuid(),
    role: ClaimableRoleSchema,
});
